import { Component, OnInit } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { portalsDataKey } from 'src/app/core/constants/constants';
import { CompanyData } from 'src/app/core/models/company-data';
import { HubUser } from 'src/app/core/models/user-data';
import { PermissionsService } from 'src/app/core/services/permissions.service';
import { UserDataService } from 'src/app/core/services/user-data.service';
import { tokenClaimsField } from '../secured-skeleton/utils/secured-skeleton-utils';
import { CustomerPortal } from '../secured-skeleton/models/customer-portal';
import {
  INotificationPreferences,
  INotificationPreferencesResponse,
} from 'src/app/core/models/notification-preferences';
import { ProfileRepositoryService } from './services/profile-repository.service';
import { finalize } from 'rxjs';
import { SnackbarComponent } from 'src/app/core/components/snackbar/snackbar.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Role } from 'src/app/shared/models/rbac-models';

const serviceEscalationAddons = ['managed-services', 'connectivity-services'];

@Component({
  selector: 'profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  standalone: false,
})
export class ProfileComponent implements OnInit {
  get userHasNocOrSocNotificationsAllowed(): boolean {
    return this.isNocAllowed || this.isSocAllowed || this.isSpsAllowed;
  }

  /** Returns the name set by the user in the azure B2C form */
  get userFullName(): string {
    const userDbName = (this.user.fullName ?? '').trim();
    return userDbName === '' ? tokenClaimsField('name', this.authService) : this.user.fullName;
  }

  /** The data of the company to which the [user] belongs */
  get companyData(): CompanyData {
    return this.userDataService.company;
  }

  /** The data of the logged user */
  get user(): HubUser {
    return this.userDataService.user;
  }

  /** Enabled Services for the current company */
  get companyEnabledServices(): string {
    const roles = this.companyData?.serviceAddons ?? [];
    return this.descriptionOfRoles(roles);
  }

  /** The list of user V2 roles */
  get accessData(): Role[] {
    return this.userDataService.user?.permissionsV2?.roles?.filter((r) => r.enabled) ?? [];
  }

  /** Whether the current user has any RBAC V2 role data assigned */
  get hasRbacV2Roles(): boolean {
    return (this.userDataService.user?.permissionsV2?.roles?.length ?? 0) > 0;
  }

  /** True if the user has any company-admin level permission (customer admin, super admin, or customers manager). */
  get isGlobalAdmin(): boolean {
    return this.permissionsService.hasCompanyAdminPermissions();
  }

  /** Number of items to display under "Your Access" — V2 enabled roles when the user is on V2, otherwise V1 addon permissions. */
  get accessItemCount(): number {
    if (this.hasRbacV2Roles) return this.accessData.length;
    if (this.isRbacV2Active) return 0;
    return this.userDataService.user?.permissions?.length ?? 0;
  }

  /** Enabled services for the logged user — V2 role descriptions when available, otherwise V1 addon names. */
  get joinedAccessData(): string {
    if (this.hasRbacV2Roles) {
      const roles = this.accessData;
      if (roles.length === 0) return 'None';
      return roles.map((role) => role.description ?? role.name ?? 'Unknown').join(', ');
    }
    if (this.isRbacV2Active) return 'None';
    const addons = this.userDataService.user?.permissions?.map((p) => p.serviceAddon) ?? [];
    return this.descriptionOfRoles(addons);
  }

  isRbacV2Active = false;

  /** Flag to control loading notification preferences */
  isLoadingNotificationPreferences = false;
  /** Flag to control loading notification update */
  isLoadingNotificationUpdate = false;

  /** Decide if the user has the option to modify noc or soc notifications */
  isNocAllowed = false;
  isSocAllowed = false;
  isSpsAllowed = false;

  /** Save fetched notification preferences */
  notificationSettings: INotificationPreferences;

  constructor(
    private userDataService: UserDataService,
    private authService: MsalService,
    private profileRepositoryService: ProfileRepositoryService,
    private snackBar: MatSnackBar,
    private permissionsService: PermissionsService,
  ) {}

  ngOnInit() {
    this.isRbacV2Active = this.userDataService.user?.permissionsVersion === 'v2';
    //Fetch Notification Preference if user has access to managed services or if its global admin
    this.userDataService.getAllowedPermissions()?.some((permission) => serviceEscalationAddons.includes(permission))
      ? this.fetchNotificationPreferences()
      : (this.isLoadingNotificationPreferences = false);
  }

  /**
   * Fetch Notification Preferences
   */
  fetchNotificationPreferences() {
    this.isLoadingNotificationPreferences = true;
    this.profileRepositoryService
      .getNotificationPreferences()
      .pipe(finalize(() => (this.isLoadingNotificationPreferences = false)))
      .subscribe({
        next: (response: INotificationPreferencesResponse) => {
          this.isNocAllowed = response.nocAllowed;
          this.isSocAllowed = response.socAllowed;
          this.isSpsAllowed = response.spsAllowed;
          this.notificationSettings = response.settings;
        },
        error: (error) => {
          this.openSnackBar('Error fetching notification preferences', 'error');
        },
      });
  }

  /** Handles the save of the notification preferences */
  saveNotificationPreferences(data: INotificationPreferences) {
    this.isLoadingNotificationUpdate = true;
    this.profileRepositoryService
      .updateNotificationPreferences(data)
      .pipe(finalize(() => (this.isLoadingNotificationUpdate = false)))
      .subscribe({
        next: (response) => {
          this.notificationSettings = response;
          // Remove the snack bar message as we are now using live editing
          // this.openSnackBar('Notification preferences saved successfully', 'success')
        },
        error: (error) => {
          this.openSnackBar('Error saving notification preferences', 'error');
        },
      });
  }

  /** Returns a user friendly description of the given roles... Null roles are excluded */
  private descriptionOfRoles(roles: string[]): string {
    if (roles.length === 0) {
      return 'None';
    }

    if (roles.includes('admin')) {
      return 'Shop, Order Tracking, LifecycleRepair';
    }

    const roleDescriptions = roles.map((role) => this.descriptionOfRole(role)).filter((role) => role !== null);
    return roleDescriptions.join(', ');
  }

  /** Transforms the given [role] into a user friendly description */
  private descriptionOfRole(role: string): string {
    const portals: CustomerPortal[] = JSON.parse(sessionStorage.getItem(portalsDataKey)).filter(
      (portal) => portal.isActive,
    );

    const addon = portals.find((portal: CustomerPortal) => portal.addonName === role);

    if (addon) {
      return addon.name;
    } else {
      return null;
    }
  }

  /** Open Snack bar for success or error messages */
  private openSnackBar(message: string, type: 'success' | 'error') {
    this.snackBar.openFromComponent(SnackbarComponent, {
      data: {
        message,
        type,
        closeButton: true,
      },
      duration: 5000,
    });
  }
}
